// AI Visibility refresh schedule. The data must be refreshed every three months
// or it goes out of date; the next update is due three calendar months after the
// latest completed run. Dates are handled as local calendar days (YYYY-MM-DD) —
// never through toISOString(), which shifts the day across the UTC boundary.

export const UPDATE_INTERVAL_MONTHS = 3;

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
const parseYmd = (ymd) => ymd.split('-').map(Number);

/** Today as a local YYYY-MM-DD. */
export const todayYmd = (now = new Date()) => toYmd(now.getFullYear(), now.getMonth() + 1, now.getDate());

/**
 * Add calendar months to a YYYY-MM-DD, clamping to the target month's last day
 * (Nov 30 + 3 months → Feb 28/29, not Mar 2).
 */
export const addMonths = (ymd, months) => {
  const [y, m, d] = parseYmd(ymd);
  const target = new Date(y, m - 1 + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return toYmd(target.getFullYear(), target.getMonth() + 1, Math.min(d, lastDay));
};

/** Whole days from `a` to `b` (negative when `b` is earlier). */
export const daysBetween = (a, b) => {
  const [ay, am, ad] = parseYmd(a);
  const [by, bm, bd] = parseYmd(b);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
};

/** The latest run_date among completed runs, or null when there is none. */
export const latestCompletedRunDate = (runs) => (Array.isArray(runs) ? runs : [])
  .filter((r) => r?.status === 'completed' && /^\d{4}-\d{2}-\d{2}/.test(r?.run_date || ''))
  .map((r) => r.run_date.slice(0, 10))
  .reduce((max, d) => (max == null || d > max ? d : max), null);

/**
 * Where this property stands on its three-month refresh.
 * @returns {{ state: 'none'|'upcoming'|'due'|'overdue', lastRun: string|null,
 *             dueDate: string|null, days: number|null }}
 *   `days` is days until the due date (0 = due today, negative = overdue by that many).
 */
export const updateStatus = (runs, now = new Date()) => {
  const lastRun = latestCompletedRunDate(runs);
  if (!lastRun) return { state: 'none', lastRun: null, dueDate: null, days: null };
  const dueDate = addMonths(lastRun, UPDATE_INTERVAL_MONTHS);
  const days = daysBetween(todayYmd(now), dueDate);
  const state = days > 0 ? 'upcoming' : days === 0 ? 'due' : 'overdue';
  return { state, lastRun, dueDate, days };
};
